import Grid from '@mui/material/Unstable_Grid2/Grid2';
import React from 'react';
import { titleToId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/ContentSectionId';
import {
  ContentCardContainer,
  ContentCardHeader,
  ContentUtilizationStatus,
  ContentTextContainer,
  ContentUtilizationVideoCard,
  ContentUtilizationVideoCardPlaceholder,
  ContentDivider,
  UtilizationGridImageCard,
  UtilizationGridImageCardPlaceholder
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/UtilizationCardContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import {
  ChartDataAdapter,
  UtilizationData
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import BeaconProChart from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';

const getCardContent = (item: UtilizationData) => {
  switch (item.type) {
    case 'character':
      return (
        <ContentTextContainer textVariant="body2" textSx={{ lineHeight: '24px' }}>
          {item.content}
        </ContentTextContainer>
      );
    case 'image':
      return (
        <Grid
          container
          sx={{
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '16px',
            display: 'grid'
          }}
        >
          {item.content.map((image, index) =>
            image ? (
              <UtilizationGridImageCard
                key={index}
                imageUri={image.url}
                mainDisplayTitle={image.altText}
                secondaryDisplayTitle={image.altText}
                metrics={{ resolution: image.resolution, ratio: image.ratio }}
              />
            ) : (
              <UtilizationGridImageCardPlaceholder key={index} />
            )
          )}
        </Grid>
      );
    case 'video':
      return (
        <Grid container sx={{ rowGap: '16px', columnGap: '16px', justifyContent: 'flex-start' }}>
          {item.content.map((video, index) =>
            video ? (
              <ContentUtilizationVideoCard
                key={index}
                videoSrc={video.url}
                metrics={{ resolution: video.resolution, length: video.length }}
              />
            ) : (
              <ContentUtilizationVideoCardPlaceholder key={index} />
            )
          )}
        </Grid>
      );
    default:
      return null;
  }
};

interface ProductLevelContentUtilizationSummaryProps {
  queryFn: () => Promise<ChartDataAdapter<UtilizationData[]>>;
  renderSubtitle: () => string | React.ReactNode;
}

export const ProductLevelContentUtilizationSummary = ({
  queryFn,
  renderSubtitle
}: ProductLevelContentUtilizationSummaryProps) => {
  const { isProductEntity } = useProductEntity();

  if (!isProductEntity) {
    return null;
  }

  return (
    <BeaconProChart name="product-level-content-utilization-summary" queryFn={queryFn}>
      <BeaconProChart.Header menuButton={null}>
        <BeaconProChart.Title>
          <Text variant="h4">Content Utilization by Element</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(_, isLoading) => {
          if (isLoading) {
            return 'Loading subtitle...';
          }

          return renderSubtitle();
        }}
      />

      <BeaconProChart.Visualization
        render={(adapter: ChartDataAdapter<UtilizationData[]>) => {
          let data = adapter.transform();
          // we do not want to show enhanced content section, but enhanced content is still used elsewhere
          data = data.filter((item) => item.type !== 'aPlusContent');
          return (
            <Flex flexDirection="column">
              {data.map((item: UtilizationData, i: number) => (
                <Flex key={`${item.type}-${i}`} flexDirection="column">
                  <ContentCardContainer key={`${item.type}-${i}`}>
                    <ContentCardHeader id={titleToId(item.title)} title={item.title}>
                      {item.type !== 'aPlusContent' && (
                        <ContentUtilizationStatus
                          currentUtilization={item.currentUtilization}
                          maxUtilization={item.maxUtilization}
                          score={item.score}
                          label={item.type as 'image' | 'video' | 'character'}
                          isOverLimit={item.type === 'character' ? item.isOverLimit : false}
                        />
                      )}
                    </ContentCardHeader>
                    {getCardContent(item)}
                  </ContentCardContainer>
                  {i < data.length - 1 && (
                    <Flex justifyContent="center">
                      <ContentDivider />
                    </Flex>
                  )}
                </Flex>
              ))}
            </Flex>
          );
        }}
      />
    </BeaconProChart>
  );
};
